<template>
  <div class="hr-zones">
    <div class="hr-zones__bar">
      <span
        v-for="row in visibleRows"
        :key="row.zone"
        class="hr-zones__segment"
        :style="{ flexGrow: row.share, backgroundColor: row.color }"
      ></span>
    </div>

    <div class="d-flex justify-content-between align-items-center gap-3 mt-2">
      <i18n-t v-if="mostly" keypath="recordings.activity.mostly_zone" tag="small" class="text-muted" scope="global">
        <template #zone>
          <strong :style="{ color: rows[mostly.zone - 1].color }">{{ $t('recordings.activity.zone', { number: mostly.zone }) }}</strong>
        </template>
        <template #percent>{{ mostly.percent }}%</template>
      </i18n-t>
      <button type="button" class="hr-zones__toggle" :aria-expanded="open" @click="open = !open">
        {{ open ? $t('recordings.activity.hide_zones') : $t('recordings.activity.view_zones') }}
        <span class="material-icons">{{ open ? 'expand_less' : 'expand_more' }}</span>
      </button>
    </div>

    <div v-if="open" class="mt-2">
      <div v-for="row in rows" :key="row.zone" class="hr-zones__row">
        <span class="hr-zones__name" :style="{ color: row.color }">{{ $t('recordings.activity.zone', { number: row.zone }) }}</span>
        <span class="hr-zones__track">
          <span class="hr-zones__fill" :style="{ width: `${row.barPercent}%`, backgroundColor: row.color }"></span>
        </span>
        <span class="hr-zones__time">{{ durationLabel(row.seconds) }}</span>
        <span class="hr-zones__range text-muted">{{ row.range }} {{ $t('recordings.activity.bpm') }}</span>
      </div>
      <small class="text-muted d-block mt-2">{{ $t('recordings.activity.zones_estimated') }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { durationLabel } from '../../../utils/recordingActivity/format';
import { mostlyZone, zoneRows, type ZoneTimes } from '../../../utils/recordingActivity/heartRateZones';

const props = defineProps<{ zones: ZoneTimes }>();

const open = ref(false);
const rows = computed(() => zoneRows(props.zones));
const visibleRows = computed(() => rows.value.filter((row) => row.share > 0));
const mostly = computed(() => mostlyZone(props.zones.seconds));
</script>

<style scoped>
.hr-zones__bar {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
}

.hr-zones__segment {
  flex-basis: 0;
  min-width: 2px;
}

.hr-zones__toggle {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--yellow, #fddf00);
  font-size: 0.85rem;
  font-weight: 700;
}

.hr-zones__toggle .material-icons {
  font-size: 18px;
}

.hr-zones__row {
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr) 4rem auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.hr-zones__track {
  height: 6px;
}

.hr-zones__fill {
  display: block;
  height: 100%;
  border-radius: 3px;
}

.hr-zones__time {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.hr-zones__range {
  text-align: right;
  white-space: nowrap;
}
</style>
